import React from 'react';
import { HiStar, HiShieldCheck, HiTrendingUp } from 'react-icons/hi';
import useScrollReveal from '../hooks/useScrollReveal';

const reasons = [
  {
    icon: <HiStar className="text-2xl" />,
    title: 'Curated Opportunities',
    description: 'We handpick land parcels with clear titles and the highest potential for ROI.',
    stat: '500+',
    statLabel: 'Plots Curated',
  },
  {
    icon: <HiShieldCheck className="text-2xl" />,
    title: 'End-to-End Expertise',
    description: 'From site selection to legal compliance and final plotting, we guide you through every step.',
    stat: '8-Step',
    statLabel: 'Process',
  },
  {
    icon: <HiTrendingUp className="text-2xl" />,
    title: 'Market Foresight',
    description: "We don't just sell land — we help you capitalize on the future of Mumbai's expansion.",
    stat: '10+',
    statLabel: 'Years Trusted',
  },
];

const WhyChooseUs = () => {
  const [sectionRef, sectionVisible] = useScrollReveal({ threshold: 0.1 });

  return (
    <section className="py-24 lg:py-32 relative overflow-hidden bg-primary-600 border-t-4 border-primary-700">
      {/* Subtle pattern */}
      <div className="absolute inset-0 opacity-[0.04]" style={{ backgroundImage: 'radial-gradient(circle, white 1px, transparent 1px)', backgroundSize: '30px 30px' }} />

      <div ref={sectionRef} className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        {/* Header — centered on green */}
        <div className={`text-center max-w-2xl mx-auto mb-16 transition-all duration-700 ${sectionVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <span className="inline-block text-[11px] tracking-[0.3em] uppercase font-semibold text-accent-300 mb-5">
            Your Trusted Partners
          </span>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-heading text-white leading-tight mb-5">
            Why Choose{' '}
            <span className="italic text-accent-300">Prime Acres?</span>
          </h2>
          <p className="text-white/50 text-base leading-relaxed font-light">
            Delivering curated investment opportunities with complete transparency and expert guidance.
          </p>
        </div>

        {/* Cards row */}
        <div className="grid md:grid-cols-3 gap-5">
          {reasons.map((reason, i) => (
            <div
              key={i}
              className={`bg-white rounded-2xl p-8 lg:p-10 relative group hover:-translate-y-2 hover:shadow-xl transition-all duration-700 ${
                sectionVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'
              }`}
              style={{ transitionDelay: sectionVisible ? `${200 + i * 150}ms` : '0ms' }}
            >
              {/* Icon */}
              <div className="w-14 h-14 rounded-2xl bg-primary-50 border border-primary-100 flex items-center justify-center text-primary-600 mb-6 group-hover:bg-primary-600 group-hover:text-white group-hover:border-primary-600 transition-all duration-500">
                {reason.icon}
              </div>

              <h3 className="font-heading text-xl lg:text-2xl text-dark-800 mb-3 leading-snug group-hover:text-primary-700 transition-colors duration-500">
                {reason.title}
              </h3>

              <p className="text-[14px] text-dark-400 font-light leading-[1.8] mb-8">
                {reason.description}
              </p>

              {/* Stat */}
              <div className="pt-5 border-t border-gray-100 flex items-baseline gap-2">
                <span className="text-2xl font-heading text-primary-600 group-hover:text-accent-500 transition-colors duration-500">{reason.stat}</span>
                <span className="text-[11px] text-dark-300 tracking-wide uppercase font-medium">{reason.statLabel}</span>
              </div>
            </div>
          ))}
        </div>

        {/* Bottom strip */}
        <div className={`mt-14 flex flex-col sm:flex-row items-center justify-between gap-6 pt-10 border-t border-white/15 transition-all duration-700 delay-500 ${sectionVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <p className="text-white/40 text-sm font-light text-center sm:text-left">
            10+ years of trusted expertise across Mumbai, Pune & Sambhajinagar
          </p>
          <button onClick={() => window.dispatchEvent(new Event('openContactModal'))} className="inline-flex items-center gap-2 bg-white text-primary-700 rounded-full px-7 py-3 text-[11px] font-semibold tracking-wider uppercase hover:bg-accent-50 hover:shadow-lg transition-all duration-500 group">
            Start Your Investment
            <svg className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
            </svg>
          </button>
        </div>
      </div>
    </section>
  );
};

export default WhyChooseUs;
